import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { USER_ROLES, PATHS } from 'src/common/constants';
import {
  SUITE_ACTION_PARAM,
  SUITE_DEEP_LINK_ACTION,
  SuiteDeepLinkAction,
} from 'src/components/SuitesTable/suiteDeepLinkActions';
import { onCheckUserCanAccessTheModule } from 'src/helpers/validationHelper';
import Callout from 'src/components/InformationCallout/Callout';
import Empty from '../Empty';
import Overlay from '../Overlay';
import Table from '../Table';
import Loader from '../Loader';
import Skeleton from 'src/components/Skeleton/Skeleton';
import { LegacyScheduleWalkthroughComponentCalendly } from '../ScheduleWalkthrough/ScheduleWalktroughComponent';
import { selectSearch } from 'src/store/selectors/search';
import { generateWalkthroughColumns } from 'src/components/WalkThroughTable/helpers';
import GenericDialogue from 'src/components/Dialogue/Dialogue';
import ChecklistPreview from 'src/components/ChecklistPreview/ChecklistPreview';
import {
  useTablePagination,
  UseTablePaginationReturn,
} from 'src/SharedCustomHooks/UseTablePagination/useTablePagination';
import {
  useWalkThroughTableOrchestrator,
  UseWalkThroughTableOrchestratorReturn,
} from './Hooks/useWalkThroughTableOrchestrator';
import './walk-through-table.scss';

const SKELETON_ROW_COUNT: number = 13;

// Deep-links up to the property's Suites view with an action the SuitesTable consumes on mount to auto-open the matching bulk dialogue.
const buildPropertyWalkthroughActionUrl = (
  propertyId: string,
  action: SuiteDeepLinkAction
): string =>
  `${PATHS.PROPERTIES_VIEW.replace(':id', propertyId)}?${SUITE_ACTION_PARAM}=${action}`;

interface WalkThroughTableProps {
  suite: any;
  property: any;
  propertyManagerDetails: any;
  refreshSuiteCounts: () => Promise<void>;
}

const WalkThroughTable = ({
  suite,
  property,
  propertyManagerDetails,
  refreshSuiteCounts,
}: WalkThroughTableProps) => {
  const search: any = useSelector(selectSearch);

  const { itemsPerPage, onItemsPerPageChange }: UseTablePaginationReturn =
    useTablePagination();

  const {
    user,
    uiState,
    walkthroughs,
    isLoading,
    deleteWalkthrough,
    handleStatusClicked,
    exportPDF,
    calendlyScheduling,
  }: UseWalkThroughTableOrchestratorReturn = useWalkThroughTableOrchestrator({
    suite,
    property,
    propertyManagerDetails,
    refreshSuiteCounts,
  });

  const onDelete = (data: any): void => {
    const confirmed: boolean = window.confirm(
      `Are you sure you want to delete ${data.walkthroughName}?`
    );

    if (!confirmed) {
      return;
    }

    void deleteWalkthrough(data.id, user.id);
  };

  const tableColumns: any = useMemo(() => {
    return generateWalkthroughColumns({
      user,
      handleStatusClicked,
      exportPDF,
      onCheckUserCanAccessTheModule,
      propertyManagerDetails,
      onDelete,
      onPreview: uiState.openPreview,
    });
  }, [user, suite, propertyManagerDetails]);

  const skeletonColumns: any[] = useMemo(
    () =>
      tableColumns.map((column: any) => ({
        ...column,
        cell: () => <Skeleton variant="text" width="70%" />,
      })),
    [tableColumns]
  );

  const skeletonData: any[] = useMemo(
    () =>
      Array.from({ length: SKELETON_ROW_COUNT }, (_, index: number) => ({
        id: `walkthrough-skeleton-${index}`,
      })),
    []
  );

  return (
    <>
      {uiState.isPdfLoading && <Loader center={true} />}
      {onCheckUserCanAccessTheModule([
        USER_ROLES.PROPERTY_DEVELOPER,
        USER_ROLES.ADMIN,
        USER_ROLES.SUPER_ADMIN,
      ]) && (
        <div className="walk-through-table-callout">
          <Callout
            variant="info"
            title="Manage walkthroughs from the property level"
            subtitle="Adding, activating, and scheduling walkthroughs now happens in bulk from the property's Suites view. This page is for reviewing this suite's existing walkthroughs."
          >
            <div className="walk-through-table-callout-actions">
              <a
                href={buildPropertyWalkthroughActionUrl(
                  property?.id,
                  SUITE_DEEP_LINK_ACTION.addWalkthroughs
                )}
                target="_blank"
                rel="noopener noreferrer"
              >
                Add walkthroughs →
              </a>
              <a
                href={buildPropertyWalkthroughActionUrl(
                  property?.id,
                  SUITE_DEEP_LINK_ACTION.activateWalkthroughs
                )}
                target="_blank"
                rel="noopener noreferrer"
              >
                Activate walkthroughs →
              </a>
            </div>
          </Callout>
        </div>
      )}

      {/* TODO: LEGACY (Calendly backwards-compat): FYI for homeowners/occupants who can still self-schedule
          via Calendly here. Remove with the Calendly scheduling flow once Cal.com supports walkthroughs. */}
      {onCheckUserCanAccessTheModule([
        USER_ROLES.HOME_OWNER,
        USER_ROLES.OCCUPANT,
      ]) && (
        <div className="walk-through-table-callout">
          <Callout
            variant="warning"
            title="Heads up: walkthrough scheduling is changing"
            subtitle="Scheduling here still runs on Calendly, which is being phased out in favour of a new system. You can book your walkthrough as usual for now — if anything looks off, your property manager can help."
          />
        </div>
      )}

      {isLoading ? (
        <Table
          columns={skeletonColumns}
          data={skeletonData}
          paginate={{ itemsPerPage, position: 'both' }}
          paginationSkeleton
          onItemsPerPageChange={onItemsPerPageChange}
        />
      ) : walkthroughs.length > 0 ? (
        <Table
          columns={tableColumns}
          data={walkthroughs}
          paginate={{ itemsPerPage, position: 'both' }}
          onItemsPerPageChange={onItemsPerPageChange}
        />
      ) : (
        <Empty
          headline="No Walkthroughs yet"
          strapline="All Walkthroughs will be listed here so it’s easier for you to manage."
          searchIsActive={search?.walkThrough || ''}
          image={
            <img alt="No Walk Throughs yet" src="/assets/empty/suites.svg" />
          }
        />
      )}

      <GenericDialogue
        isOpen={!!uiState.previewItem}
        onClose={uiState.closePreview}
        title="Walkthrough Checklist Preview (Read only)"
        description={uiState.previewItem?.walkthroughName}
        maxWidth={'50rem'}
      >
        <ChecklistPreview
          items={uiState.previewItem?.walkthroughChecklistItems || []}
        />
      </GenericDialogue>

      {/* LEGACY (Calendly backwards-compat): suite-walkthrough self-scheduling overlay.
          Remove with useCalendlyWalkthroughScheduling once Cal.com supports walkthroughs. */}
      <Overlay
        showOverlay={calendlyScheduling.isScheduleOverlayOpen}
        content={
          <LegacyScheduleWalkthroughComponentCalendly
            suite={suite}
            property={propertyManagerDetails}
            scheduling_url={propertyManagerDetails?.calendlyLink}
            hasCalendlyKey={!!propertyManagerDetails?.calendlyAPIKey}
            onEventScheduled={calendlyScheduling.onWalkthroughScheduled}
            onClose={calendlyScheduling.closeScheduleOverlay}
            onFetchAvailableServiceTimes={
              calendlyScheduling.fetchAvailableServiceTimes
            }
            availableServiceTimes={calendlyScheduling.availableServiceTimes}
            user={user}
          />
        }
      />
    </>
  );
};

export default WalkThroughTable;
